import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Umbrella, Envelope, ThermometerSimple, Timer, Plus, Trash, Info } from "@phosphor-icons/react";

const TEMPLATE_META = {
  frost: {
    title: "Frost / Wetter — Absage",
    hint: "Wird verschickt, wenn die Wetter-Automatik Frost erkennt oder Sie manuell auslösen.",
    vars: ["customer_name", "tenant_name", "job_date", "min_temp_c"],
  },
  vehicle_breakdown: {
    title: "Fahrzeugausfall — Absage",
    hint: "Kurzfristige Absage wegen technischer Probleme.",
    vars: ["customer_name", "tenant_name", "job_date"],
  },
  replacement: {
    title: "Ersatztermin",
    hint: 'Wird automatisch verschickt, wenn Sie einen Ersatztermin planen (Button „Ersatztermin" beim Job).',
    vars: ["customer_name", "tenant_name", "old_date", "new_date"],
  },
  vacation: {
    title: "Betriebsurlaub",
    hint: "Vorab-Info an alle betroffenen Kunden über den Urlaubszeitraum.",
    vars: ["customer_name", "tenant_name", "start", "end", "resume"],
  },
};

const CANCEL_MODES = [
  { v: "24h", l: "24 Stunden vor Reinigungstermin" },
  { v: "day_before_2359", l: "23:59 Uhr am Vortag" },
  { v: "hourly", l: "Individuell (Stunden vor Termin)" },
];

export default function OperationsPanel({ tenant }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [savedMsg, setSavedMsg] = useState(null);
  const [newVac, setNewVac] = useState({ start_date: "", end_date: "", note: "" });
  const [activeTemplate, setActiveTemplate] = useState("frost");
  const [notice, setNotice] = useState(Number(tenant?.notice_period_months) || 3);
  const [stateCode, setStateCode] = useState((tenant?.holiday_state_code || "BW").toUpperCase());

  useEffect(() => {
    setNotice(Number(tenant?.notice_period_months) || 3);
    setStateCode((tenant?.holiday_state_code || "BW").toUpperCase());
  }, [tenant?.notice_period_months, tenant?.holiday_state_code]);

  useEffect(() => {
    if (!tenant?.tenant_id) return;
    api.get(`/tenants/${tenant.tenant_id}/operations`).then((r) => setData(r.data));
  }, [tenant?.tenant_id]);

  const save = async (patch) => {
    setBusy(true);
    try {
      const r = await api.patch(`/tenants/${tenant.tenant_id}/operations`, patch);
      setData(r.data);
      setSavedMsg({ ok: true, msg: "Gespeichert" });
      setTimeout(() => setSavedMsg(null), 2500);
    } catch (e) {
      setSavedMsg({ ok: false, msg: e.response?.data?.detail || "Fehler" });
    } finally { setBusy(false); }
  };

  const addVacation = async () => {
    if (!newVac.start_date || !newVac.end_date) return;
    const list = [...(data.vacation_periods || []), newVac];
    setNewVac({ start_date: "", end_date: "", note: "" });
    await save({ vacation_periods: list });
  };
  const removeVacation = async (id) => {
    const list = (data.vacation_periods || []).filter((v) => v.id !== id);
    await save({ vacation_periods: list });
  };

  if (!data) return <div className="text-sm text-zinc-400 p-6">Lade Betriebs-Einstellungen…</div>;

  const tpl = data.mail_templates[activeTemplate] || { subject: "", body: "" };
  const setTemplate = (field, value) => {
    setData({ ...data, mail_templates: { ...data.mail_templates, [activeTemplate]: { ...tpl, [field]: value } } });
  };
  const saveTemplate = () => save({ mail_templates: { [activeTemplate]: data.mail_templates[activeTemplate] } });

  // Contract & Holiday settings (top-level tenant)
  const saveTenant = async (patch) => {
    setBusy(true);
    try {
      const r = await api.patch(`/tenants/${tenant.tenant_id}`, patch);
      Object.assign(tenant, r.data || {});
      setSavedMsg({ ok: true, msg: "Gespeichert" });
      setTimeout(() => setSavedMsg(null), 2500);
    } catch (e) {
      setSavedMsg({ ok: false, msg: e.response?.data?.detail || "Fehler" });
    } finally { setBusy(false); }
  };
  const DE_STATES = [
    ["BW","Baden-Württemberg"],["BY","Bayern"],["BE","Berlin"],["BB","Brandenburg"],
    ["HB","Bremen"],["HH","Hamburg"],["HE","Hessen"],["MV","Mecklenburg-Vorpommern"],
    ["NI","Niedersachsen"],["NW","Nordrhein-Westfalen"],["RP","Rheinland-Pfalz"],
    ["SL","Saarland"],["SN","Sachsen"],["ST","Sachsen-Anhalt"],["SH","Schleswig-Holstein"],["TH","Thüringen"],
  ];

  return (
    <div className="space-y-6" data-testid="operations-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Betrieb & Kommunikation</h2>
          <p className="text-sm text-zinc-500 mt-1">Betriebsurlaub, Absage-Fristen, Wetter-Automatik und Mail-Vorlagen.</p>
        </div>
        {savedMsg && (
          <span className={`text-xs font-medium ${savedMsg.ok ? "text-emerald-700" : "text-red-600"}`}>
            {savedMsg.ok ? "✓" : "✗"} {savedMsg.msg}
          </span>
        )}
      </div>

      {/* Vertrags- & Feiertags-Standards */}
      <section className="bg-white border border-black/10 p-5" data-testid="contract-defaults-panel">
        <h3 className="font-heading text-lg font-semibold tracking-tight mb-1">Vertrags-Standards & Feiertage</h3>
        <p className="text-xs text-zinc-500 mb-4">Standard-Kündigungsfrist für neue Abos (überschreibbar pro Abo) sowie Bundesland für die Feiertags-Automatik im Schichtplan.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Kündigungsfrist (Monate)</Label>
            <div className="flex gap-2 items-center">
              <Input type="number" min={0} max={24} value={notice} onChange={(e) => setNotice(e.target.value)} className="w-32" data-testid="tenant-notice-months" />
              <Button size="sm" variant="outline" disabled={busy} onClick={() => saveTenant({ notice_period_months: Number(notice) || 3 })} data-testid="save-tenant-notice">Speichern</Button>
            </div>
            <p className="text-[10px] text-zinc-400 mt-1">Restlaufzeit nach Kündigung — Reinigungen werden bis zum Ablauf weiter geplant.</p>
          </div>
          <div>
            <Label className="text-xs">Bundesland (Feiertage im Schichtplan)</Label>
            <div className="flex gap-2 items-center">
              <select value={stateCode} onChange={(e) => setStateCode(e.target.value)} className="h-9 px-3 rounded-md border border-black/10 text-sm bg-white" data-testid="tenant-state-code">
                {DE_STATES.map(([v,l]) => <option key={v} value={v}>{`${v} — ${l}`}</option>)}
              </select>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => saveTenant({ holiday_state_code: stateCode })} data-testid="save-tenant-state">Speichern</Button>
            </div>
            <p className="text-[10px] text-zinc-400 mt-1">Steuert farbige Markierung und „Frei"-Vorschläge an gesetzlichen Feiertagen.</p>
          </div>
        </div>
      </section>
      <section className="bg-white border border-black/10 p-5" data-testid="vacation-panel">
        <h3 className="font-heading text-lg font-semibold tracking-tight flex items-center gap-2 mb-3">
          <Umbrella size={18} weight="bold" /> Betriebsurlaub
        </h3>
        <p className="text-xs text-zinc-500 mb-4">
          Erscheint automatisch in Rechnungen, PDF-Dokumenten und E-Mail-Signaturen. In den definierten Zeiträumen werden keine Touren generiert.
        </p>

        <div className="space-y-2 mb-4">
          {(data.vacation_periods || []).length === 0 ? (
            <div className="text-xs text-zinc-400 italic">Kein Urlaub konfiguriert.</div>
          ) : (
            data.vacation_periods.map((v) => (
              <div key={v.id} className="flex flex-wrap items-center gap-3 p-3 rounded-md bg-amber-50 border border-amber-200" data-testid={`vacation-item-${v.id}`}>
                <div className="font-mono text-sm">
                  {new Date(v.start_date).toLocaleDateString("de-DE")} – {new Date(v.end_date).toLocaleDateString("de-DE")}
                </div>
                {v.note && <div className="text-sm text-zinc-600 italic">„{v.note}"</div>}
                <button
                  onClick={() => removeVacation(v.id)}
                  className="ml-auto w-8 h-8 rounded-md hover:bg-red-100 text-red-600 flex items-center justify-center"
                  data-testid={`remove-vacation-${v.id}`}
                >
                  <Trash size={14} weight="bold" />
                </button>
              </div>
            ))
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_2fr_auto] gap-2 items-end">
          <div>
            <Label className="text-xs">Von</Label>
            <Input type="date" className="h-10 rounded-none" value={newVac.start_date} onChange={(e) => setNewVac({ ...newVac, start_date: e.target.value })} data-testid="vacation-start-input" />
          </div>
          <div>
            <Label className="text-xs">Bis</Label>
            <Input type="date" className="h-10 rounded-none" value={newVac.end_date} onChange={(e) => setNewVac({ ...newVac, end_date: e.target.value })} data-testid="vacation-end-input" />
          </div>
          <div>
            <Label className="text-xs">Notiz (optional)</Label>
            <Input className="h-10 rounded-none" placeholder="z.B. Sommerferien" value={newVac.note} onChange={(e) => setNewVac({ ...newVac, note: e.target.value })} data-testid="vacation-note-input" />
          </div>
          <Button onClick={addVacation} disabled={busy || !newVac.start_date || !newVac.end_date} className="rounded-none bg-zinc-900 text-white hover:bg-black h-10" data-testid="add-vacation-btn">
            <Plus size={13} weight="bold" className="mr-1" /> Hinzufügen
          </Button>
        </div>
      </section>

      {/* Absage-Fristen */}
      <section className="bg-white border border-black/10 p-5" data-testid="cancellation-panel">
        <h3 className="font-heading text-lg font-semibold tracking-tight flex items-center gap-2 mb-3">
          <Timer size={18} weight="bold" /> Absage-Frist für Kunden
        </h3>
        <p className="text-xs text-zinc-500 mb-4">
          Bis wann darf der Kunde einen Reinigungstermin selbstständig absagen? Danach zählt der Termin als bestätigt und wird berechnet.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[280px]">
            <Label className="text-xs">Regel</Label>
            <Select
              value={data.cancellation_policy?.mode || "24h"}
              onValueChange={(mode) => save({ cancellation_policy: { mode, hours: mode === "hourly" ? (data.cancellation_policy?.hours || 24) : undefined } })}
            >
              <SelectTrigger className="h-10 rounded-none" data-testid="cancellation-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CANCEL_MODES.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {data.cancellation_policy?.mode === "hourly" && (
            <div className="w-40">
              <Label className="text-xs">Stunden vorher</Label>
              <Input
                type="number"
                min={1}
                max={168}
                className="h-10 rounded-none"
                value={data.cancellation_policy?.hours || 24}
                onChange={(e) => setData({ ...data, cancellation_policy: { mode: "hourly", hours: parseInt(e.target.value) || 24 } })}
                onBlur={() => save({ cancellation_policy: data.cancellation_policy })}
                data-testid="cancellation-hours-input"
              />
            </div>
          )}
        </div>
      </section>

      {/* Wetter-Automatik */}
      <section className="bg-gradient-to-br from-sky-50 to-white border border-black/10 p-5" data-testid="weather-panel">
        <h3 className="font-heading text-lg font-semibold tracking-tight flex items-center gap-2 mb-3">
          <ThermometerSimple size={18} weight="bold" /> Frost-/Wetter-Automatik
        </h3>
        <p className="text-xs text-zinc-500 mb-4">
          Prüft täglich um 04:30 (via DWD/Bright Sky), ob morgen unter dem Grenzwert liegt. Bei Frostgefahr erscheint eine Warnung im Backoffice — Reinigungen können nicht garantiert werden (Wasser gefriert).
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr_auto] gap-4 items-end">
          <label className="flex items-center gap-2 h-10">
            <input
              type="checkbox"
              checked={!!data.weather?.enabled}
              onChange={(e) => save({ weather: { ...data.weather, enabled: e.target.checked } })}
              className="w-5 h-5"
              data-testid="weather-enabled-checkbox"
            />
            <span className="text-sm font-medium">Automatik aktiv</span>
          </label>
          <div>
            <Label className="text-xs">Grenzwert Tiefsttemperatur (°C)</Label>
            <Input
              type="number"
              step="0.5"
              className="h-10 rounded-none w-40"
              value={data.weather?.min_temp_c ?? 0}
              onChange={(e) => setData({ ...data, weather: { ...data.weather, min_temp_c: parseFloat(e.target.value) } })}
              onBlur={() => save({ weather: data.weather })}
              disabled={!data.weather?.enabled}
              data-testid="weather-min-temp-input"
            />
          </div>
          {data.weather?.last_check && (
            <div className="text-xs text-zinc-600">
              Letzter Check: <span className="font-mono">{data.weather.last_check.min_temp_c ?? "—"} °C</span>{" "}
              {data.weather.last_check.frost_risk ? (
                <span className="text-red-600 font-bold ml-1">⚠ Frostrisiko</span>
              ) : (
                <span className="text-emerald-600 font-bold ml-1">✓ OK</span>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Mail-Templates */}
      <section className="bg-white border border-black/10 p-5" data-testid="mail-templates-panel">
        <h3 className="font-heading text-lg font-semibold tracking-tight flex items-center gap-2 mb-3">
          <Envelope size={18} weight="bold" /> E-Mail-Vorlagen
        </h3>
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(TEMPLATE_META).map(([k, m]) => (
            <button
              key={k}
              onClick={() => setActiveTemplate(k)}
              className={`text-xs px-3 py-2 rounded-full border transition ${activeTemplate === k ? "bg-black text-white border-black" : "bg-white border-black/10 text-zinc-600 hover:bg-zinc-50"}`}
              data-testid={`tpl-tab-${k}`}
            >
              {m.title}
            </button>
          ))}
        </div>
        <div className="mb-3 flex items-start gap-2 p-3 rounded bg-zinc-50 border border-black/5 text-xs text-zinc-600">
          <Info size={14} weight="fill" className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <div>{TEMPLATE_META[activeTemplate].hint}</div>
            <div className="mt-1">
              Verfügbare Platzhalter:{" "}
              {TEMPLATE_META[activeTemplate].vars.map((v) => (
                <code key={v} className="mx-1 px-1.5 py-0.5 bg-white border border-black/10 rounded font-mono">{`{${v}}`}</code>
              ))}
            </div>
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Betreff</Label>
            <Input
              className="h-11 rounded-none font-mono text-sm"
              value={tpl.subject}
              onChange={(e) => setTemplate("subject", e.target.value)}
              data-testid={`tpl-${activeTemplate}-subject`}
            />
          </div>
          <div>
            <Label className="text-xs">Nachricht</Label>
            <Textarea
              rows={10}
              className="rounded-none font-mono text-sm"
              value={tpl.body}
              onChange={(e) => setTemplate("body", e.target.value)}
              data-testid={`tpl-${activeTemplate}-body`}
            />
          </div>
          <div className="flex justify-end">
            <Button onClick={saveTemplate} disabled={busy} className="rounded-none bg-black text-white hover:bg-zinc-800" data-testid={`tpl-${activeTemplate}-save`}>
              {busy ? "Speichere…" : "Vorlage speichern"}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
